import { Link, useLocation } from '@tanstack/react-router'
import * as React from 'react'

import { useMe } from '@/api/hooks/useMe'
import { IndividualSwitcher } from '@/components/individual-switcher'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar'
import { useNavigationConfig } from '@/config/useNavigationConfig'
import { useAppDispatch } from '@/redux/hooks'
import { AssistantActions } from '@/redux/reducers/assistant'

import { FeedbackButton } from './feedback-button'
import { UnlockDeveloperModeTrigger } from './molecules/unlock-developer-mode-trigger'
import { NavUser } from './nav-user'
import { SubscribeSidebarCard } from './subscribe-sidebar-card'

export const AppSidebar = ({ ...props }: React.ComponentProps<typeof Sidebar>) => {
  const location = useLocation()
  const pathname = location.pathname
  const { data: user } = useMe()
  const navigationConfig = useNavigationConfig()
  const { isMobile, setOpenMobile } = useSidebar()
  const dispatch = useAppDispatch()

  React.useEffect(() => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }, [pathname, isMobile, setOpenMobile])

  const handleMobileNavigation = () => {
    if (!isMobile) return

    dispatch(AssistantActions.toggleSideChat(false))
    setOpenMobile(false)
  }

  return (
    <Sidebar {...props}>
      <SidebarHeader>
        <IndividualSwitcher />
        {/* <SearchForm /> */}
      </SidebarHeader>
      <SidebarContent>
        {/* We create a SidebarGroup for each parent. */}
        {navigationConfig.navMain.map(item => (
          <SidebarGroup key={item.title}>
            {!item.hideTitle && <SidebarGroupLabel>{item.title}</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {item.items.map(item => (
                  <UnlockDeveloperModeTrigger enabled={item.url === '/settings'} key={item.title}>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={pathname.startsWith(item.url)}
                        onClick={handleMobileNavigation}
                        size="lg"
                      >
                        <Link to={item.url}>
                          {item.icon}
                          <span>{item.title}</span>
                          {item.badge && (
                            <span className="ml-auto size-2 rounded-full bg-brand-mullbar" />
                          )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </UnlockDeveloperModeTrigger>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarSeparator className="mx-0 bg-sidebar-divider" />
      <SidebarFooter>
        <SubscribeSidebarCard />
        <FeedbackButton />
        {user && (
          <NavUser
            user={{
              name: `${user.firstName} ${user.lastName}`,
            }}
          />
        )}
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
